import React, { useState } from 'react';
import {
  Alert,
  Dialog,
  DialogContent,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { ShowWasSaved } from '../core/ShowWasSaved';
import {
  DataGrid,
  GridColDef,
  GridValueGetterParams,
  deDE,
} from '@mui/x-data-grid';
import { ISystemMail } from '../Interfaces/ISystemMail';
import { SystemMailEdit } from './SystemMailEdit';
import { Box } from '@mui/system';
import { EmailSignatureEdit } from '../Admin/Settings/EmailSignatureEdit';
import { ISystemMailCategory } from '../Interfaces/ISystemMailCategory';
import { ISystemMail_has_Dictionary } from '../Interfaces/ISystemMail_has_Dictionary';
import { Check } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { State } from '../redux/mainReducer';
import { LoadingSpinner } from '../core/LoadingSpinner';
import {
  useGetSystemMails,
  useGetSystemMailCategories,
  useGetSystemMailHasDictionary,
} from '../hooks/reactQuerys/SystemMailQueries';

export const SystemMailOverview = () => {
  const { systemMails, isLoadingSystemMails } = useGetSystemMails();
  const { systemMailCategories, isLoadingSystemMailCategories } =
    useGetSystemMailCategories();
  const { systemMailHasDictionary, isLoadingSystemMailHasDictionary } =
    useGetSystemMailHasDictionary();

  if (
    isLoadingSystemMails ||
    isLoadingSystemMailCategories ||
    isLoadingSystemMailHasDictionary
  ) {
    return <LoadingSpinner />;
  }

  if (!systemMails || !systemMailCategories || !systemMailHasDictionary) {
    return <Alert severity='error'>Fehler!</Alert>;
  }

  return (
    <SystemMail
      initialSystemMails={systemMails}
      systemMailCategories={systemMailCategories}
      systemMailHasDictionary={systemMailHasDictionary}
    />
  );
};

type IProps = {
  initialSystemMails: ISystemMail[];
  systemMailCategories: ISystemMailCategory[];
  systemMailHasDictionary: ISystemMail_has_Dictionary[];
};

export const SystemMail = ({
  initialSystemMails,
  systemMailCategories,
  systemMailHasDictionary,
}: IProps) => {
  const dictionaryEntryArray = useSelector(
    (state: State) => state.dictionaryEntryArray.storeDictionaryEntry
  );
  const [systemMails, setSystemMails] = useState(initialSystemMails);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [idSystemMail, setIdSystemMail] = useState<number | null>(null);
  const [wasSaved, setWasSaved] = useState(false);
  const [wasSavedSuccesfully, setWasSavedSuccesfully] = useState(false);

  const scopes = localStorage.getItem('scope')?.split(',');

  const columns: GridColDef[] = [
    { field: 'idSystemMail', headerName: 'Nr.', width: 90 },
    {
      field: 'idSystemMailCategory',
      headerName: 'Kategorie',
      valueGetter: (params: GridValueGetterParams) =>
        systemMailCategories?.find(
          x => x.idSystemMailCategory === params.row.idSystemMailCategory
        )?.SystemMailCategory,
      width: 200,
    },
    {
      field: 'isPublic',
      headerName: 'Öffentlicher Server',
      renderCell: params => (Boolean(params.row.isPublic) ? <Check /> : ''),
      width: 150,
    },
    {
      field: 'Variable',
      headerName: 'Variable',
      valueGetter: (params: GridValueGetterParams) =>
        systemMailHasDictionary
          ?.filter(x => x.idSystemMail === params.row.idSystemMail)
          .map(dictonary =>
            dictionaryEntryArray === undefined
              ? 0
              : dictionaryEntryArray?.filter(
                  x => x.idDictionary === dictonary.idDictionary
                ).length
          )
          .reduce((a, b) => a + b, 0),

      width: 100,
    },
    { field: 'Title', headerName: 'Bezeichnung', flex: 1 },
  ];

  return (
    <>
      <ShowWasSaved
        wasSaved={wasSaved}
        setWasSaved={setWasSaved}
        wasSuccessfullySaved={wasSavedSuccesfully}
      />

      <Dialog
        open={idSystemMail !== null}
        onClose={() => setIdSystemMail(null)}
        maxWidth='lg'
        fullWidth
      >
        <DialogContent>
          {idSystemMail !== null && (
            <SystemMailEdit
              key={`idSystemMail-${idSystemMail}`}
              currentId={idSystemMail}
              setCurrentId={setIdSystemMail}
              currentArray={systemMails}
              setCurrentArray={setSystemMails}
              setWasSaved={setWasSaved}
              setWasSavedSuccesfully={setWasSavedSuccesfully}
              systemMailHasDictionaryArray={systemMailHasDictionary}
            />
          )}
        </DialogContent>
      </Dialog>

      <Typography variant='h4'>Systemnachrichten</Typography>

      {scopes !== undefined && scopes.indexOf('admin') > -1 && (
        <EmailSignatureEdit />
      )}

      <Typography variant='h5' sx={{ mt: 5 }}>
        E-Mail-Vorlagen
        <TextField
          label='Kategorie'
          size='small'
          select
          sx={{ float: 'right', width: 200 }}
          value={selectedCategory}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setSelectedCategory(Number(event.target.value))
          }
        >
          <MenuItem key={`systemmailcategory-0`} value={0}>
            Alle
          </MenuItem>
          {systemMailCategories?.map(x => (
            <MenuItem
              key={`systemmailcategory-${x.idSystemMailCategory}`}
              value={x.idSystemMailCategory}
            >
              {x.SystemMailCategory}
            </MenuItem>
          ))}
        </TextField>
      </Typography>

      <Box sx={{ mt: 2 }} />
      <DataGrid
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
        rows={systemMails
          .filter(x => !x.isHidden)
          .filter(x =>
            selectedCategory === 0
              ? true
              : x.idSystemMailCategory === selectedCategory
          )}
        columns={columns}
        getRowId={row => row.idSystemMail}
        onRowClick={params => setIdSystemMail(Number(params.id))}
      />
    </>
  );
};
